import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart, 
  Users, 
  CheckCircle, 
  TrendingUp,
  ArrowRight,
  FileText,
  Target
} from 'lucide-react';
import SEO from '../../components/SEO';

const CausesHome = () => {
  const [stats, setStats] = useState({
    total_petitions: 0,
    total_signatures: 0,
    total_resolved: 0
  });
  const [featuredPetitions, setFeaturedPetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
    fetchFeaturedPetitions();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/petitions/public/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFeaturedPetitions = async () => {
    try {
      const response = await axios.get(`${API}/api/petitions/public/approved?limit=6`);
      setFeaturedPetitions(response.data.petitions);
    } catch (error) {
      console.error('Error fetching petitions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="causes-home">
      <SEO 
        title="Elderra Causes - Petition Portal for Seniors"
        description="Start a petition for grievance redressal. Connect with supporters and make your voice heard."
      />

      {/* Hero Section */}
      <section className="causes-hero">
        <div className="container">
          <div className="causes-hero-content">
            <h1 className="causes-hero-title">
              Your Voice Matters
            </h1>
            <p className="causes-hero-subtitle">
              Start a petition for grievance redressal. Connect with supporters across India and make change happen.
            </p>
            
            <div className="causes-hero-actions">
              <Link to="/causes/start" className="btn-primary-large">
                <FileText size={20} />
                Start a Cause
              </Link>
              <Link to="/causes/petitions" className="btn-secondary-large">
                <Target size={20} />
                Browse Petitions
              </Link>
            </div>

            {/* Stats Bar */}
            <div className="causes-stats-bar">
              <div className="causes-stat-item">
                <div className="stat-number">{stats.total_petitions}</div>
                <div className="stat-label">Active Causes</div>
              </div>
              <div className="causes-stat-item">
                <div className="stat-number">{stats.total_signatures.toLocaleString()}</div>
                <div className="stat-label">Signatures</div>
              </div>
              <div className="causes-stat-item">
                <div className="stat-number">{stats.total_resolved}</div>
                <div className="stat-label">Resolved</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title-center">How It Works</h2>
          <p className="section-subtitle-center">
            Three simple steps to make your voice heard
          </p>

          <div className="how-it-works-grid">
            <div className="how-it-works-card">
              <div className="step-number">1</div>
              <FileText size={48} color="#e5266a" />
              <h3>Create Your Petition</h3>
              <p>Share your grievance, target person, and what change you want to see</p>
            </div>

            <div className="how-it-works-card">
              <div className="step-number">2</div>
              <Users size={48} color="#2196f3" />
              <h3>Gather Support</h3>
              <p>Share with community members and collect signatures for your cause</p>
            </div>

            <div className="how-it-works-card">
              <div className="step-number">3</div>
              <CheckCircle size={48} color="#4caf50" />
              <h3>Make Change Happen</h3>
              <p>Present your petition to decision-makers and get resolution</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Petitions */}
      <section className="featured-petitions-section">
        <div className="container">
          <div className="section-header-flex">
            <div>
              <h2 className="section-title">Featured Causes</h2>
              <p className="section-subtitle">Support these active petitions</p>
            </div>
            <Link to="/causes/petitions" className="view-all-link">
              View All
              <ArrowRight size={20} />
            </Link>
          </div>

          {loading ? (
            <div className="loading-petitions">
              <p>Loading petitions...</p>
            </div>
          ) : featuredPetitions.length === 0 ? (
            <div className="no-petitions">
              <FileText size={64} color="#e0e0e0" />
              <h3>No Active Petitions Yet</h3>
              <p>Be the first to start a cause!</p>
              <Link to="/causes/start" className="btn-primary-large">
                Start a Cause
              </Link>
            </div>
          ) : (
            <div className="petitions-grid">
              {featuredPetitions.map((petition) => (
                <Link 
                  key={petition.id} 
                  to={`/causes/petition/${petition.id}`}
                  className="petition-card"
                >
                  <div className="petition-card-header">
                    <span className="petition-status-badge active">Active</span>
                    {petition.signature_count > 0 && (
                      <span className="petition-signatures">
                        <Users size={16} />
                        {petition.signature_count} signatures
                      </span>
                    )}
                  </div>

                  <h3 className="petition-card-title">{petition.title}</h3>
                  
                  {petition.target_person && (
                    <div className="petition-target">
                      <Target size={16} />
                      <span>To: {petition.target_person}</span>
                    </div>
                  )}

                  <p className="petition-card-excerpt">
                    {petition.grievance_details.substring(0, 150)}...
                  </p>

                  <div className="petition-card-footer">
                    <span className="petition-campaigner">
                      by {petition.about_campaigner.substring(0, 50)}
                    </span>
                    <span className="petition-action">
                      Sign Now <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="causes-cta-section">
        <div className="container">
          <div className="causes-cta-content">
            <Heart size={64} color="#e5266a" />
            <h2>Ready to Make a Difference?</h2>
            <p>
              Join thousands of seniors raising their voices for change. 
              Start your petition today and connect with supporters across India.
            </p>
            <Link to="/causes/start" className="btn-primary-large">
              <FileText size={20} />
              Start Your Petition
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CausesHome;
